import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useFairTracker } from '../data/FairTrackerStore';
import { Check, Empty, Pill, Stars } from '../ui';
import {
  landed,
  money,
  priceLine,
  riskFlags,
  supplierDecision,
  supplierRating,
} from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

const MAX = 5;

/**
 * Compare (spec § 5.13) — up to five suppliers side by side.
 *
 * It NEVER auto-picks a winner, by design. Landed cost, risk flags and lead
 * time do not reduce to one score, and a tool that pretended otherwise would
 * be making a sourcing decision the buyer is responsible for.
 *
 * The first column is sticky so the row labels stay readable while scrolling
 * sideways — on a phone that scroll is the only way to see five columns, and
 * it is the one place in the app where sideways scrolling is allowed.
 */
export default function CompareScreen() {
  const history = useHistory();
  const location = useLocation();
  const { fair, visibleSuppliers } = useFairTracker();

  const params = new URLSearchParams(location.search);
  const ids = (params.get('ids') || '').split(',').filter(Boolean);

  // Shortlist first — these are the ones worth comparing.
  const candidates = [...visibleSuppliers].sort(
    (a, b) => (b.inShortlist ? 1 : 0) - (a.inShortlist ? 1 : 0),
  );
  const selected = ids.map((id) => visibleSuppliers.find((s) => s.id === id)).filter(Boolean);

  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(0, MAX);
    history.replace(`${p('/compare')}?ids=${next.join(',')}`);
  };

  const rows = [
    ['Product', (s) => firstProduct(s) ? firstProduct(s).name : '—'],
    ['Quoted price', (s) => (firstProduct(s) ? priceLine(firstProduct(s), fair) || '—' : '—')],
    [
      'Landed cost / unit',
      (s) => {
        const l = landed(s);
        return l.perUnit != null ? money(l.perUnit) : '—';
      },
    ],
    ['MOQ', (s) => (firstProduct(s) && firstProduct(s).moq) || (s.review || {}).ot_moq || '—'],
    [
      'Production time',
      (s) => (firstProduct(s) && firstProduct(s).productionTime) || (s.review || {}).ot_lead || '—',
    ],
    [
      'Payment',
      (s) => {
        const r = s.review || {};
        const prod = firstProduct(s);
        const terms = r.ot_method || (prod && prod.paymentTerms) || '';
        const dep = r.ot_deposit ? `${r.ot_deposit}% deposit` : '';
        return [terms, dep].filter(Boolean).join(' · ') || '—';
      },
    ],
    ['Warranty', (s) => (s.review || {}).w_length || '—'],
    [
      'Certifications',
      (s) => {
        const r = s.review || {};
        const prod = firstProduct(s);
        const types = (prod && (prod.certTypes || []).join(', ')) || r.c_type || '';
        return (
          <>
            {types || '—'}
            {r.c_status ? (
              <>
                {' '}
                <Pill>{r.c_status}</Pill>
              </>
            ) : null}
          </>
        );
      },
    ],
    ['Sample', (s) => (firstProduct(s) && firstProduct(s).sample) || '—'],
    ['Supplier type', (s) => (s.review || {}).sv_type || s.type || '—'],
    [
      'Rating',
      (s) => (supplierRating(s) > 0 ? <Stars value={supplierRating(s)} readOnly /> : '—'),
    ],
    [
      'Risk flags',
      (s) => {
        const flags = riskFlags(s);
        if (!flags.length) return <Pill tone="strong">{tr("None")}</Pill>;
        return (
          <>
            <Pill tone="maybe">{flags.length}</Pill>
            <div className="ft-faint" style={{ whiteSpace: 'normal' }}>
              {flags.slice(0, 2).join('; ')}
              {flags.length > 2 ? '…' : ''}
            </div>
          </>
        );
      },
    ],
    ['Final decision', (s) => (s.decision ? <Pill>{s.decision}</Pill> : '—')],
  ];

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Compare suppliers")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {tr(`Pick up to ${MAX}.`)} {tr('This never picks a winner for you.')}
        </p>
      </div>

      {!candidates.length && <Empty title={tr("Nothing to compare yet")} />}

      <section className="ft-card">
        <h2 className="ft-h2">{tr("Choose suppliers")}</h2>
        <div className="ft-grid-2" style={{ marginTop: 8 }}>
          {candidates.map((s) => (
            <Check
              key={s.id}
              checked={ids.includes(s.id)}
              onChange={() => toggle(s.id)}
              label={`${s.company}${s.inShortlist ? ' ★' : ''}`}
            />
          ))}
        </div>
        {ids.length >= MAX && (
          <p className="ft-hint">
            {MAX} is the maximum — any more and the columns stop being readable.
          </p>
        )}
      </section>

      {selected.length < 2 ? (
        <Empty title={tr("Select at least two")}>{tr("Tick two or more suppliers to see them side by side.")}</Empty>
      ) : (
        <div className="ft-tablewrap">
          <table className="ft-table ft-table-sticky">
            <thead>
              <tr>
                <th>&nbsp;</th>
                {selected.map((s) => (
                  <th key={s.id}>
                    <Link to={p(`/s/${s.id}`)}>{s.company}</Link>
                    <div className="ft-faint">
                      {[s.booth ? `Booth ${s.booth}` : null, supplierDecision(s)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, get]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  {selected.map((s) => (
                    <td key={s.id}>{get(s)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected.length >= 2 && (
        <div className="ft-btnrow" style={{ marginTop: 12 }}>
          <Link className="ft-btn" to={p('/shortlist')}>
            {tr("Back to shortlist")}
          </Link>
        </div>
      )}
    </div>
  );
}

const firstProduct = (s) =>
  (s.products || []).find((x) => x.decision === 'Strong') || (s.products || [])[0] || null;
